import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { PolarisVizProvider } from '@shopify/polaris-viz'
import { NavMenu } from '@shopify/app-bridge-react'
import '@shopify/polaris-viz/build/esm/styles.css'

// Hooks
import { useShopifyAuth } from './hooks/useShopifyAuth'

// Pages
import Dashboard from './pages/Dashboard'
import Analytics from './pages/Analytics'
import Flow from './pages/Flow'
import History from './pages/History'

// Create a query client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

/**
 * Navigation component using App Bridge React's NavMenu.
 * Per Shopify docs, use plain <a> tags with href, NOT React Router Link.
 * This renders navigation in Shopify Admin's sidebar.
 *
 * DEBUG: Adding extensive logging to diagnose why nav isn't appearing.
 */
function AppNavigation() {
  console.log('[AppNavigation] Rendering NavMenu component')
  console.log('[AppNavigation] window.shopify:', window.shopify)
  console.log('[AppNavigation] window.shopify?.environment:', window.shopify?.environment)
  console.log('[AppNavigation] window.shopify?.config?.shop:', window.shopify?.config?.shop)

  // Check if ui-nav-menu is defined
  const isNavMenuDefined = !!customElements.get('ui-nav-menu')
  console.log('[AppNavigation] ui-nav-menu custom element defined:', isNavMenuDefined)

  // Log after render to see if element is in DOM
  setTimeout(() => {
    const navElement = document.querySelector('ui-nav-menu')
    console.log('[AppNavigation] After render - ui-nav-menu in DOM:', !!navElement)
    if (navElement) {
      console.log('[AppNavigation] ui-nav-menu innerHTML:', navElement.innerHTML)
      console.log('[AppNavigation] ui-nav-menu attributes:', Array.from(navElement.attributes).map(a => `${a.name}=${a.value}`))
    }
  }, 1000)

  return (
    <NavMenu>
      <a href="/" rel="home">Home</a>
      <a href="/analytics">Analytics</a>
      <a href="/flow">Flow</a>
      <a href="/history">History</a>
    </NavMenu>
  )
}

// Main app content with routing
function AppContent() {
  return (
    <>
      <AppNavigation />
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/flow" element={<Flow />} />
        <Route path="/history" element={<History />} />
      </Routes>
    </>
  )
}

// Wrapper component that initializes Shopify auth
function AuthenticatedApp() {
  // Initialize Shopify auth check
  const { isInShopifyAdmin } = useShopifyAuth()

  // If not in Shopify Admin, show instructions
  if (!isInShopifyAdmin) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <h1 style={{ marginBottom: '16px' }}>Otty AI</h1>
        <p style={{ color: '#637381' }}>
          This app must be opened from the Shopify Admin.
        </p>
        <p style={{ color: '#637381', marginTop: '8px' }}>
          Go to your Shopify store admin and open the app from there.
        </p>
      </div>
    )
  }

  return <AppContent />
}

// Main App component
// App Bridge v4 is initialized automatically via script tag when loaded in Shopify Admin
function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <PolarisVizProvider>
          <AuthenticatedApp />
        </PolarisVizProvider>
      </QueryClientProvider>
    </BrowserRouter>
  )
}

export default App
